/**
 * GSCSettings - Manage GSC integration settings
 *
 * Uses Path 2: Direct GSC API connection (no Google Ads linkage required)
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { debug } from '@/utils/debug';
import { formatSiteUrl } from '@/utils/urlUtils';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui';
import { Slider } from '@/components/ui/Slider';
import { gscApi, getConnectionStatus } from '@/services/api/gsc';
import { GSCConnectionModal } from './GSCConnectionModal';
import type { GSCIntegrationSettings } from '@/types/gsc';

interface GSCSettingsProps {
  customerId?: string;
  /** Whether to show the link status card. Default true. Set to false when link management is handled elsewhere. */
  showLinkStatus?: boolean;
}

export function GSCSettings({ customerId, showLinkStatus = true }: GSCSettingsProps) {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [isConnectionModalOpen, setIsConnectionModalOpen] = useState(false);
  const [isChangingProperty, setIsChangingProperty] = useState(false);
  const [localSettings, setLocalSettings] = useState<Partial<GSCIntegrationSettings>>({});
  const [devMode, setDevMode] = useState(debug.isDev());

  // Listen for debug mode changes
  useEffect(() => {
    const unsubscribe = debug.onModeChange(() => setDevMode(debug.isDev()));
    return unsubscribe;
  }, []);

  // Fetch current settings
  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ['gsc-settings'],
    queryFn: gscApi.getSettings,
  });

  // Fetch GSC connection status (Path 2: direct GSC API)
  const { data: connectionStatus, isLoading: connectionLoading } = useQuery({
    queryKey: ['gsc-connection-status'],
    queryFn: getConnectionStatus,
  });

  // Fetch import status
  const { data: importStatus } = useQuery({
    queryKey: ['gsc-import-status'],
    queryFn: gscApi.getImportStatus,
  });

  // Enable mutation - uses Path 2 (direct GSC API) by default, Path 1 if customerId provided
  const enableMutation = useMutation({
    mutationFn: () => {
      // Path 2: Direct GSC API (no customerId required)
      // Uses the site_url already stored from OAuth flow
      if (!customerId) {
        return gscApi.enableIntegrationDirect();
      }
      // Path 1: Google Ads linkage (legacy, requires customerId)
      return gscApi.enableIntegration(customerId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gsc-settings'] });
      queryClient.invalidateQueries({ queryKey: ['gsc-import-status'] });
      queryClient.invalidateQueries({ queryKey: ['gsc-connection-status'] });
    },
  });

  // Disable/Unlink mutation
  const disableMutation = useMutation({
    mutationFn: gscApi.disableIntegration,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gsc-settings'] });
      queryClient.invalidateQueries({ queryKey: ['gsc-connection-status'] });
    },
  });

  // Update settings mutation
  const updateMutation = useMutation({
    mutationFn: gscApi.updateSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gsc-settings'] });
      setIsEditing(false);
    },
  });

  const handleEnable = () => {
    // With Path 2, we enable when GSC OAuth is completed and property is selected
    if (connectionStatus?.oauth_completed && connectionStatus?.site_url) {
      enableMutation.mutate();
    }
  };

  const handleConnectionSuccess = (_siteUrl: string) => {
    // Refetch settings after successful connection
    queryClient.invalidateQueries({ queryKey: ['gsc-settings'] });
    queryClient.invalidateQueries({ queryKey: ['gsc-connection-status'] });
  };

  const handleDisable = () => {
    disableMutation.mutate();
  };

  const handleSaveSettings = () => {
    updateMutation.mutate(localSettings);
  };

  const startEditing = () => {
    setLocalSettings({
      incrementality_floor: settings?.incrementality_floor,
      incrementality_slope: settings?.incrementality_slope,
      incrementality_model: settings?.incrementality_model,
    });
    setIsEditing(true);
  };

  if (settingsLoading || connectionLoading) {
    return (
      <Card>
        <div className="p-6 text-center text-gray-500">Loading GSC settings...</div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Link Status Card - only shown when showLinkStatus is true */}
      {showLinkStatus && (
        <Card>
          <div className="p-6">
            <div className="flex items-center gap-4 mb-4">
              <span className="text-gray-600">Status:</span>
              {connectionStatus?.site_url ? (
                <Badge variant="success">Linked</Badge>
              ) : connectionStatus?.oauth_completed ? (
                <Badge variant="warning">OAuth Complete - Select Property</Badge>
              ) : (
                <Badge variant="secondary">Not Linked</Badge>
              )}
            </div>

            {connectionStatus?.site_url && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-600">Linked property:</span>
                <div className="font-medium">{formatSiteUrl(connectionStatus.site_url)}</div>
              </div>
            )}

            {/* Not linked - show link button */}
            {!connectionStatus?.site_url && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Link your Google Search Console to see organic search data alongside your Google Ads performance.
                  This helps identify keywords where you have strong organic presence.
                </p>
                <Button onClick={() => {
                  setIsChangingProperty(false);
                  setIsConnectionModalOpen(true);
                }}>
                  Link Google Search Console
                </Button>
              </div>
            )}

            {/* Connected but not enabled */}
            {connectionStatus?.site_url && !settings?.gsc_enabled && (
              <Button
                onClick={handleEnable}
                disabled={enableMutation.isPending}
              >
                {enableMutation.isPending ? 'Enabling...' : 'Enable GSC Integration'}
              </Button>
            )}

            {/* Enabled - show disable button */}
            {settings?.gsc_enabled && (
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  onClick={handleDisable}
                  disabled={disableMutation.isPending}
                >
                  {disableMutation.isPending ? 'Unlinking...' : 'Unlink'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setIsChangingProperty(true);
                    setIsConnectionModalOpen(true);
                  }}
                >
                  Change Property
                </Button>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* GSC Connection Modal - only needed when showLinkStatus is true */}
      {showLinkStatus && (
        <GSCConnectionModal
          isOpen={isConnectionModalOpen}
          onClose={() => {
            setIsConnectionModalOpen(false);
            setIsChangingProperty(false);
          }}
          onConnected={handleConnectionSuccess}
          isChangingProperty={isChangingProperty}
        />
      )}

      {/* Integration Settings Card - Dev mode only */}
      {settings?.gsc_enabled && devMode && (
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Incrementality Settings</h3>
              {!isEditing && (
                <Button variant="secondary" onClick={startEditing}>
                  Edit Settings
                </Button>
              )}
            </div>

            <p className="text-sm text-gray-500 mb-4">
              These settings control how organic presence affects bid optimization.
              Higher organic share leads to lower incrementality (more cannibalization).
            </p>

            <div className="space-y-6">
              {/* Floor setting */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Incrementality Floor: {(isEditing ? localSettings.incrementality_floor : settings.incrementality_floor)?.toFixed(2) ?? '0.30'}
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Minimum incrementality even at 100% organic share. Higher = less aggressive.
                </p>
                {isEditing ? (
                  <Slider
                    value={(localSettings.incrementality_floor ?? 0.3) * 100}
                    onChange={(v) => setLocalSettings(s => ({ ...s, incrementality_floor: v / 100 }))}
                    min={10}
                    max={80}
                    step={5}
                  />
                ) : (
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${(settings.incrementality_floor || 0.3) * 100}%` }}
                    />
                  </div>
                )}
              </div>

              {/* Slope setting */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Incrementality Slope: {(isEditing ? localSettings.incrementality_slope : settings.incrementality_slope)?.toFixed(2) ?? '0.70'}
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  How much incrementality decreases per unit organic share. Higher = more aggressive.
                </p>
                {isEditing ? (
                  <Slider
                    value={(localSettings.incrementality_slope ?? 0.7) * 100}
                    onChange={(v) => setLocalSettings(s => ({ ...s, incrementality_slope: v / 100 }))}
                    min={30}
                    max={100}
                    step={5}
                  />
                ) : (
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${(settings.incrementality_slope || 0.7) * 100}%` }}
                    />
                  </div>
                )}
              </div>

              {/* Preview */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-700 mb-2">Incrementality Preview</h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>At 0% organic: <strong>100%</strong> incremental</p>
                  <p>At 50% organic: <strong>
                    {(Math.max(
                      (isEditing ? localSettings.incrementality_floor : settings.incrementality_floor) ?? 0.3,
                      1 - 0.5 * ((isEditing ? localSettings.incrementality_slope : settings.incrementality_slope) ?? 0.7)
                    ) * 100).toFixed(0)}%
                  </strong> incremental</p>
                  <p>At 100% organic: <strong>
                    {(((isEditing ? localSettings.incrementality_floor : settings.incrementality_floor) ?? 0.3) * 100).toFixed(0)}%
                  </strong> incremental</p>
                </div>
              </div>

              {isEditing && (
                <div className="flex gap-2">
                  <Button onClick={handleSaveSettings} disabled={updateMutation.isPending}>
                    {updateMutation.isPending ? 'Saving...' : 'Save Settings'}
                  </Button>
                  <Button variant="secondary" onClick={() => setIsEditing(false)}>
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Import Status Card - Dev mode only */}
      {settings?.gsc_enabled && devMode && importStatus && importStatus.length > 0 && (
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-4">Import Status</h3>
            <div className="space-y-3">
              {importStatus.map((status) => (
                <div key={status.import_type} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <div>
                    <span className="font-medium">{status.import_type}</span>
                    {status.last_import_date && (
                      <span className="text-sm text-gray-500 ml-2">
                        Last import: {(() => {
                          const [year, month, day] = status.last_import_date.split('-').map(Number);
                          return new Date(year, month - 1, day).toLocaleDateString();
                        })()}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">
                      {status.rows_imported.toLocaleString()} rows
                    </span>
                    <Badge
                      variant={
                        status.status === 'completed' ? 'success' :
                        status.status === 'failed' ? 'error' :
                        status.status === 'running' ? 'primary' : 'secondary'
                      }
                    >
                      {status.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Backfill Status - Dev mode only */}
      {settings?.gsc_enabled && devMode && !settings.backfill_completed && (
        <Card>
          <div className="p-6 bg-blue-50">
            <h3 className="text-lg font-semibold text-blue-800 mb-2">Historical Data Backfill</h3>
            <p className="text-blue-700">
              Historical data backfill is in progress. This may take several minutes.
              You can continue using the app while backfill completes in the background.
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}

export default GSCSettings;
